import { useQuery } from '@tanstack/react-query'
import type { CalculatedRole } from '@viapeople/api/src/shared/shared.types'
import { cn, PinIcon, Text } from '@viapeople/components'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { sessionStore } from '../../sharedStores/sessionStore'
import { trpc } from '../../utils/trpc'
import { CycleSection } from './CycleSection'
import { EnpsSurveyCard } from './EnpsSurveyCard'
import { PecSurveyCard } from './PecSurveyCard'
import { TaskCategoryCard } from './TaskCategoryCard'
import { getCyclesDetails, getCycleValues } from './utils'

const PageHeader = ({ userName }: { userName: string }) => {
	return (
		<div className='flex mb-5'>
			<Text variant='h3' weight='semibold'>
				Hello{` ${userName}`}
			</Text>
		</div>
	)
}

type DashboardTab = 'active' | 'completed'

export function DashboardPageTable() {
	const sessionState = sessionStore.getState()
	const navigate = useNavigate()
	const [activeTab, setActiveTab] = useState<DashboardTab>('active')

	// Get cycles data
	const { data: cyclesData } = useQuery({
		...trpc.client.dashboard.getCyclesInfo.queryOptions(),
		enabled: !!sessionState.user?.organizationId,
	})

	// Get task summary grouped by role
	const { data: taskSummary, isLoading: isSummaryLoading } = useQuery({
		...trpc.client.dashboard.getTaskSummaryByRole.queryOptions({}),
		enabled: !!sessionState.user?.organizationId,
		refetchOnWindowFocus: true,
	})

	// Filter summaries based on active tab
	const activeSummaries = taskSummary?.summaries?.filter((s) => s.incomplete > 0) || []
	const completedSummaries = taskSummary?.summaries?.filter((s) => s.completed > 0 || s.declined > 0) || []
	const displayedSummaries = activeTab === 'active' ? activeSummaries : completedSummaries

	// Get pending eNPS surveys
	const { data: pendingEnpsSurveys } = useQuery({
		...trpc.client.dashboard.getPendingEnpsSurveys.queryOptions(),
		enabled: !!sessionState.user?.organizationId,
	})

	// Get pending PEC surveys
	const { data: pendingPecSurveys } = useQuery({
		...trpc.client.dashboard.getPendingPecSurveys.queryOptions(),
		enabled: !!sessionState.user?.organizationId,
	})

	const cyclesDetails = getCyclesDetails(cyclesData)
	const headers = ['Cycle name', 'Employees', 'Cycle Steps', 'Status', 'Current Step', 'Next Step']
	const values = getCycleValues(cyclesDetails)

	// Check if there are any eNPS surveys
	const hasEnpsSurveys = pendingEnpsSurveys && pendingEnpsSurveys.length > 0

	// Check if there are any PEC surveys
	const hasPecSurveys = pendingPecSurveys && pendingPecSurveys.length > 0

	// Empty state check depends on active tab
	const showEmptyState =
		activeTab === 'active'
			? activeSummaries.length === 0 && !hasEnpsSurveys && !hasPecSurveys
			: completedSummaries.length === 0

	return (
		<main className='w-full p-6'>
			<PageHeader userName={`${sessionState.user?.firstName || ''} ${sessionState.user?.lastName || ''}`} />

			{/* Tab Navigation */}
			<div className='flex border-b border-neutral-200 mb-6'>
				<button
					type='button'
					role='tab'
					aria-selected={activeTab === 'active'}
					onClick={() => setActiveTab('active')}
					className={cn(
						'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
						activeTab === 'active'
							? 'border-primary-600 text-primary-600'
							: 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300',
					)}>
					Active
				</button>
				<button
					type='button'
					role='tab'
					aria-selected={activeTab === 'completed'}
					onClick={() => setActiveTab('completed')}
					className={cn(
						'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
						activeTab === 'completed'
							? 'border-primary-600 text-primary-600'
							: 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300',
					)}>
					Completed
				</button>
			</div>

			{/* Task Cards Section */}
			<div className='space-y-4 mb-6'>
				{isSummaryLoading && (
					<div className='text-center py-8'>
						<Text variant='small' className='text-neutral-500'>
							Loading tasks...
						</Text>
					</div>
				)}

				{!isSummaryLoading && showEmptyState && (
					<div className='border border-neutral-200 rounded-lg p-12 text-center'>
						<PinIcon className='w-12 h-12 mx-auto mb-4 text-neutral-400' />
						<Text variant='h6' weight='semibold' className='mb-2'>
							{activeTab === 'active' ? 'No tasks to display' : 'No completed tasks'}
						</Text>
						<Text variant='small' className='text-neutral-500'>
							{activeTab === 'active' ? 'You are up to date!' : 'Complete some tasks to see them here.'}
						</Text>
					</div>
				)}

				{displayedSummaries.map((summary) => (
					<TaskCategoryCard
						key={summary.role || 'no-role'}
						role={summary.role as CalculatedRole | null}
						incomplete={summary.incomplete}
						notStarted={summary.notStarted}
						inProgress={summary.inProgress + (summary.rejected || 0)}
						completed={summary.completed + (summary.declined || 0)}
						late={summary.late}
						isCompletedView={activeTab === 'completed'}
					/>
				))}

				{/* eNPS Survey Cards - Only show in active tab */}
				{activeTab === 'active' &&
					pendingEnpsSurveys?.map((survey) => (
						<EnpsSurveyCard
							key={survey.executionId}
							surveyName={survey.surveyName}
							questionText={survey.questionText}
							launchedAt={survey.launchedAt}
							onClick={() => navigate(`/enps/respond/${survey.executionId}`)}
						/>
					))}

				{/* PEC Survey Cards - Only show in active tab */}
				{activeTab === 'active' &&
					pendingPecSurveys?.map((survey) => (
						<PecSurveyCard
							key={survey.pecCycleId}
							cycleName={survey.cycleName}
							pendingCount={survey.pendingCount}
							totalCount={survey.totalCount}
							launchedAt={survey.launchedAt}
							onClick={() => navigate(`/pec/survey/${survey.pecCycleId}`)}
						/>
					))}
			</div>

			{/* Cycles Section */}
			<CycleSection
				title='Cycles'
				values={values}
				headers={headers}
				onRowClick={(cycleId: string) => {
					navigate(`/dashboard/cycle/${cycleId}`)
				}}
			/>
		</main>
	)
}
